/**
 * Step 4 "Log" (Sofia, Oct 24 2026): where everyone on a role stands, in five stages — Shortlisted ·
 * InMail sent · Follow-up sent · Replied · Booked (Nov 2 2026: a booking through the InMail's
 * screening link, which hands them over to Lever). Each person is in exactly one stage, the furthest.
 *
 * "Follow-up sent" is DERIVED, not observed (Sofia: "leite es ab"): LinkedIn sends its own follow-up
 * from the composer after the set days unless they reply, and nothing tells us it went out. So a sent
 * person without a reply whose follow-up date has passed counts as followed up — unless the follow-up
 * was closed (no reply / by hand) before that date.
 */
import type { LeverWrite, OutreachCandidate } from '../../types/taOutreach';

export type LogStage = 'shortlisted' | 'sent' | 'followed_up' | 'replied' | 'booked';
export const LOG_STAGES: LogStage[] = ['shortlisted', 'sent', 'followed_up', 'replied', 'booked'];

export interface LogEntry {
  candidate: OutreachCandidate;
  stage: LogStage;
  /** When the stage was reached (follow-up: its due date). */
  at: string | null;
  /** One line on where they are inside the stage. */
  note: string;
}

const DAY_MS = 86_400_000;
const NOT_SENT_NOTE: Record<string, string> = {
  shortlisted: 'On the shortlist, not decided yet',
  imported: 'Accepted · InMail not written yet',
  analysed: 'Accepted · InMail not written yet',
  queued: 'Accepted · InMail being written',
  drafted: 'Accepted · draft waiting for you',
  ready: 'Approved · not sent yet',
};

const fmt = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/Berlin' });
/** "5 Nov, 10:30" in Berlin time: when the booked call is. */
export const fmtBookedAt = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });

/** When the follow-up is (or was) due: its own date, else sent + the role's follow-up days. */
export function followUpDueAt(c: OutreachCandidate, followUpDays: number): string | null {
  if (c.followUp?.dueAt) return c.followUp.dueAt;
  if (!c.sentAt) return null;
  return new Date(Date.parse(c.sentAt) + followUpDays * DAY_MS).toISOString();
}

/** The person's stage, or null for someone not on the log (dropped, or an agent misfit). */
export function logEntryOf(c: OutreachCandidate, now: number, followUpDays: number): LogEntry | null {
  if (c.status === 'sent') {
    if (c.booking) {
      return { candidate: c, stage: 'booked', at: c.booking.at, note: c.booking.start ? `Booked for ${fmtBookedAt(c.booking.start)}` : 'Booked' };
    }
    if (c.reply) return { candidate: c, stage: 'replied', at: c.reply.at, note: 'Replied' };
    const due = followUpDueAt(c, followUpDays);
    const dueMs = due ? Date.parse(due) : NaN;
    const closedMs = c.followUp?.closedAt ? Date.parse(c.followUp.closedAt) : NaN;
    // Closed (no reply / by hand) before the date: the follow-up never went out.
    const stopped = Number.isFinite(closedMs) && closedMs < dueMs;
    if (due && dueMs <= now && !stopped) {
      // Only LinkedIn's own follow-up goes out by itself; without it, the recruiter sends theirs.
      const byLinkedIn = c.followUp ? c.followUp.scheduledInLinkedIn !== false : true;
      return {
        candidate: c,
        stage: 'followed_up',
        at: due,
        note: byLinkedIn ? `Follow-up sent by LinkedIn (due ${fmt(due)})` : `Follow-up due ${fmt(due)} — yours to send`,
      };
    }
    return {
      candidate: c,
      stage: 'sent',
      at: c.sentAt,
      note: stopped ? 'InMail sent · follow-up stopped' : due ? `InMail sent · follow-up ${fmt(due)}` : 'InMail sent',
    };
  }
  if (c.status === 'dropped') return null;
  // An agent misfit has not been shortlisted: it stays in "Not a fit" on Find people.
  if (c.status === 'shortlisted' && c.sourcing?.skipReason) return null;
  return { candidate: c, stage: 'shortlisted', at: c.sourcing?.savedAt || c.createdAt, note: NOT_SENT_NOTE[c.status] || 'Shortlisted' };
}

/** Everyone on the role's log, by stage, newest first in each. */
/** The stage names, shared by the Log and the funnel strip above the steps. */
export const LOG_STAGE_LABEL: Record<LogStage, string> = {
  shortlisted: 'Shortlisted',
  sent: 'InMail sent',
  followed_up: 'Follow-up sent',
  replied: 'Replied',
  booked: 'Booked',
};

export function buildLog(items: OutreachCandidate[], now: number, followUpDays: number): Record<LogStage, LogEntry[]> {
  const out: Record<LogStage, LogEntry[]> = { shortlisted: [], sent: [], followed_up: [], replied: [], booked: [] };
  for (const c of items) {
    const e = logEntryOf(c, now, followUpDays);
    if (e) out[e.stage].push(e);
  }
  for (const s of LOG_STAGES) out[s].sort((a, b) => Date.parse(b.at || '') - Date.parse(a.at || ''));
  return out;
}
/**
 * What happened in Lever after the booking (proxy: lib/outreachBooking.js → lib/outreachLeverWrite.js).
 * The handover to Lever is where the outreach for this person ends. In test mode nothing is written —
 * it says what would have happened. Since Nov 2 2026 a reply alone hands nobody over: a replied row
 * says something about Lever only when a handover was in fact started (a booking, an older reply, or
 * the reply switch on the proxy), or when the reply was read from Lever.
 */
export function leverHandoverText(c: Pick<OutreachCandidate, 'reply' | 'leverWrite' | 'booking'>): { text: string; tone: 'done' | 'info' | 'warn' } | null {
  const w: LeverWrite | null | undefined = c.leverWrite;
  if (!c.booking && !c.reply) return null;
  if (!c.booking && c.reply?.source === 'lever') return { text: 'Already in Lever (Responded)', tone: 'done' };
  if (!w) return c.booking ? { text: 'Not handed over to Lever yet', tone: 'info' } : null;
  switch (w.state) {
    case 'done':
      if (w.action === 'already_past') return { text: 'Already further along in Lever — nothing changed', tone: 'done' };
      if (w.action === 'already_responded') return { text: 'Already in Lever (Responded)', tone: 'done' };
      return { text: 'Handed over to Lever — the outreach ends here', tone: 'done' };
    case 'test':
      return {
        text: `Test mode: would have been ${w.action === 'would_move' ? 'moved to Responded' : 'created'} in Lever — nothing written`,
        tone: 'info',
      };
    case 'pending':
      return { text: 'Handing over to Lever…', tone: 'info' };
    case 'needs_recruiter':
      return { text: `Lever: needs you — ${w.detail || 'several people match'}`, tone: 'warn' };
    case 'failed':
      return { text: `Lever handover failed (${w.attempts ?? 1}/3), retried automatically`, tone: 'warn' };
    default:
      return null;
  }
}
